'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSession } from 'next-auth/react';
import { useQueryClient } from '@tanstack/react-query';
import { Plus } from '@phosphor-icons/react';
import {
  fetchOrgCombiningInfo,
  linkOrgForCombining,
  onboardLinkedOrg,
  unlinkOrgForCombining,
  type OrgMembershipItem,
} from '@/apis/crossOrgCombining';
import {
  invalidateGithubOrgCache,
  searchGithubOrgs,
  type GithubMemberOrg,
} from '@/apis/searchProviderOrganization';
import { ChooseRepositoriesModal } from '@/app/(onboard)/onboarding/components/team-insight-flow/ChooseRepositoriesModal';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/useToast';
import { useUserLogged } from '@/hooks/useUserLogged';
import { useSwitchActiveOrg } from '@/hooks/useSwitchActiveOrg';

type Row = {
  orgUuid: string;
  /** The workspace's own name in OrganizationModel. Kept for identification
   * only -- it is human-editable free text and is NOT what this list shows. */
  orgName: string;
  /** What the list actually renders: the GitHub org's name, with its real
   * casing. This list is about GitHub orgs, so it must name them exactly as
   * GitHub does; a workspace display name can be edited to anything and has
   * already caused a real org to be mistaken for one we knew about. Falls back
   * to orgName only when an org has no git org recorded at all. */
  gitOrgLabel: string;
  isHome: boolean;
  isLinked: boolean;
  gitOrgLogins: string[];
};

export interface LinkedOrgInfo {
  orgUuid: string;
  orgName: string;
}

interface LinkedOrgsCardProps {
  /** Reports the current set of linked orgs (home included when linked) up
   * to the parent, e.g. so Settings -> General can show each one's name +
   * UUID next to the caller's own. Pass a stable setState dispatch, not an
   * inline callback -- an unstable reference re-fires this every render. */
  onLinkedOrgsChange?: (orgs: LinkedOrgInfo[]) => void;
}

/**
 * Personal, reversible cross-org data combining (14 Aug proposal).
 *
 * Distinct from GitOrgNameSettingsCard: that card links GitHub orgs INTO one
 * workspace's own git_org_name scope, org-wide, admin-only. This one combines
 * OTHER independently-onboarded workspaces' dashboard data into the caller's
 * own personal view, per-user, reversible, without changing which org they
 * are authenticated as. Deliberately not merged into that card's UI or logic.
 *
 * One org can be in exactly one of three states here:
 *   - never onboarded by anyone -- shown as an "Onboard" button, which runs
 *     onboardLinkedOrg then the normal repo-selection flow. Onboarding does
 *     NOT auto-link; the org becomes toggleable afterward, same as any
 *     already-onboarded org.
 *   - onboarded (by the caller or anyone else who's a GitHub member), not
 *     currently linked into the caller's own view -- a toggle, off.
 *   - onboarded and linked -- a toggle, on. The home org gets one too once
 *     a second org is linked (there's otherwise nothing to unlink it FROM).
 */
export default function LinkedOrgsCard({
  onLinkedOrgsChange,
}: LinkedOrgsCardProps) {
  const { user, organization } = useUserLogged();
  const { data: session } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { switchTo, switching } = useSwitchActiveOrg();

  /**
   * Drop every cached dashboard response after the linked-org set changes.
   *
   * Query keys carry the active org UUID but not the linked-org set, so
   * nothing about a link/unlink invalidates them on its own: the dashboards
   * keep serving data fetched under the previous scope, and the user sees
   * another org's numbers until staleTime lapses or they hard-reload. That
   * reads as combining being broken, when the backend was already returning
   * the right scope all along -- verified live: the API reported the linked
   * set as the newly linked org alone while the page still showed 768 PRs
   * from the org that had just been unlinked.
   *
   * Invalidating everything rather than an enumerated list on purpose: the
   * combined scope feeds nearly every org-scoped surface in the app, and a
   * hand-maintained key list would silently rot as new dashboards land --
   * exactly the failure this is fixing.
   */
  const invalidateOrgScopedCaches = useCallback(() => {
    void queryClient.invalidateQueries();
  }, [queryClient]);

  const [info, setInfo] = useState<{
    otherMemberships: OrgMembershipItem[];
    linkedOrgUuids: string[];
  } | null>(null);
  const [eligibleOrgs, setEligibleOrgs] = useState<GithubMemberOrg[] | null>(
    null
  );
  const [busyOrg, setBusyOrg] = useState<string | null>(null);
  const [repoModalOrg, setRepoModalOrg] = useState<{
    orgUuid: string;
    label: string;
    memberUserUuid: string;
    gitOrgLogin: string;
    gitOrgId?: string;
  } | null>(null);
  // Set synchronously, unlike state, so a guard checked immediately after a
  // click (before React re-renders) still sees the in-flight write.
  const writeInFlightRef = useRef(false);
  // True from the moment the caller leaves for GitHub's app settings until the
  // list has been refetched on their return. Gating on it keeps the refetch
  // off every incidental tab focus, which would put a GitHub API call behind
  // every alt-tab for everyone.
  const awaitingOrgGrantRef = useRef(false);
  // Monotonic id for org-listing reads. Dropping the cache does not cancel a
  // request already in flight, so without this the mount read can resolve
  // AFTER the post-grant refresh and overwrite the fresh listing with the
  // pre-grant one -- including its empty-on-failure result, which would hide
  // the org the caller just granted.
  const orgsReadIdRef = useRef(0);

  const homeOrgUuid = organization?.OrgUUID ?? '';
  const homeOrgName = organization?.OrgName ?? '';
  // Only the home org's OWN GitHub login, never a comma-separated Path A
  // multi-org scope. Under the one-org-per-workspace model an org's
  // git_org_name is its own login, so a single token is all that can
  // legitimately be "already known" here. Treating extra tokens as known
  // hid genuinely un-onboarded orgs: a workspace still carrying a legacy
  // Path A scope (e.g. git_org_name "NXT-2026" on a different workspace)
  // silently suppressed NXT-2026's own Onboard row.
  const homeGitOrgLogins = (organization?.GitOrgName ?? '')
    .split(',')
    .slice(0, 1)
    .map((login) => login.trim().toLowerCase())
    .filter(Boolean);
  // Real casing, for display. homeGitOrgLogins below lowercases for matching.
  const homeGitOrgLabel =
    (organization?.GitOrgName ?? '').split(',')[0]?.trim() ||
    homeGitOrgLogins[0] ||
    homeOrgName;
  const sessionProvider = (session?.user?.provider ?? '').toLowerCase();
  const accessToken = (session?.user as { accessToken?: string } | undefined)
    ?.accessToken;

  const loadInfo = useCallback(async () => {
    const result = await fetchOrgCombiningInfo();
    if (result.Error) return;
    setInfo({
      otherMemberships: result.OtherOrgMemberships,
      linkedOrgUuids: result.LinkedOrgUUIDs,
    });
  }, []);

  useEffect(() => {
    loadInfo();
  }, [loadInfo]);

  /**
   * @param forceRefresh Drop the per-token cache first. Required after a grant
   *   on github.com: that does not change the token, so the cached listing
   *   would be replayed and the newly approved org would never show up.
   */
  const loadEligibleOrgs = useCallback(
    async (forceRefresh = false) => {
      if (sessionProvider !== 'github' || !accessToken) return;
      const readId = (orgsReadIdRef.current += 1);
      if (forceRefresh) invalidateGithubOrgCache(accessToken);
      try {
        const orgs = await searchGithubOrgs('', accessToken);
        if (readId === orgsReadIdRef.current) setEligibleOrgs(orgs);
      } catch {
        if (readId === orgsReadIdRef.current) setEligibleOrgs([]);
      }
    },
    [sessionProvider, accessToken]
  );

  // GitHub only: onboarding a not-yet-known org needs the org listing,
  // fetched with the session's own OAuth token.
  useEffect(() => {
    // Through loadEligibleOrgs, not a bare searchGithubOrgs call, so this read
    // shares the generation counter with the post-grant refresh and cannot
    // land on top of it.
    void loadEligibleOrgs();
  }, [loadEligibleOrgs]);

  /**
   * Open GitHub's settings for this OAuth app, where the caller grants it
   * access to an org that enforces third-party application restrictions.
   *
   * A new tab, not a redirect: granting takes a moment on GitHub and can end
   * in a request an org owner has to approve, and navigating away would throw
   * away whatever the caller was part-way through in Settings.
   */
  const openOrgAccessSettings = useCallback(() => {
    awaitingOrgGrantRef.current = true;
    window.open('/api/github/org-access', '_blank', 'noopener,noreferrer');
  }, []);

  // Newly granted orgs only appear on a fresh /user/orgs read, and nothing
  // tells this tab that a grant happened in another one. Refetching when the
  // caller comes back turns "I granted access and the list still looks the
  // same" into the list simply being right.
  useEffect(() => {
    const onFocus = () => {
      if (!awaitingOrgGrantRef.current) return;
      awaitingOrgGrantRef.current = false;
      void loadEligibleOrgs(true);
      void loadInfo();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadEligibleOrgs, loadInfo]);

  const rows: Row[] = info
    ? [
        {
          orgUuid: homeOrgUuid,
          orgName: homeOrgName,
          gitOrgLabel: homeGitOrgLabel,
          isHome: true,
          isLinked: info.linkedOrgUuids.includes(homeOrgUuid),
          gitOrgLogins: homeGitOrgLogins,
        },
        ...info.otherMemberships.map((m) => ({
          orgUuid: m.OrgUUID,
          orgName: m.OrgName || m.OrgUUID,
          // GitOrgName is the git org with real casing; GitOrgLogins is the
          // lowercased matching form. The type says it: match on the logins,
          // show this.
          // GitOrgLogins before OrgName: the API permits GitOrgName and
          // GitOrgLogins independently, and a lowercased real login still
          // names the GitHub org, whereas OrgName is editable free text that
          // may name something else entirely. CodeManagement resolves install
          // targets the same way. OrgName stays the last resort.
          gitOrgLabel:
            m.GitOrgName || m.GitOrgLogins?.[0] || m.OrgName || m.OrgUUID,
          isHome: false,
          isLinked: info.linkedOrgUuids.includes(m.OrgUUID),
          gitOrgLogins: (m.GitOrgLogins ?? []).map((l) => l.toLowerCase()),
        })),
      ]
    : [];
  const linkedCount = rows.filter((r) => r.isLinked).length;
  // Match a raw GitHub login (from searchGithubOrgs) against a known
  // membership by its GitOrgLogins first (reliable: the backend values an
  // onboarded org was actually created from -- more than one for a Path A
  // multi-org workspace, so each is flattened into its own set entry, not
  // compared as one composite string) and its display OrgName as a
  // fallback (covers the home org, whose GitOrgLogins isn't fetched here).
  // onboardLinkedOrg's collision-avoiding "{anchor} ({login})" display name
  // never equals the raw login, so OrgName alone under-matches for any org
  // onboarded through this flow.
  // The display NAME is only a fallback for a row that has no real login, and
  // never alongside one. A workspace's OrgName is free text: Freshworks' home
  // org is named "Freshworks" while its git org is `freshdesk`, so folding the
  // name in marked the genuinely separate GitHub org `freshworks` as already
  // known and removed its Onboard row entirely -- for every member, since this
  // is derived from org data rather than anyone's token. GitHub returned the
  // org correctly; we discarded it.
  //
  // Same failure mode the comment above records for Path A scopes: treating
  // something as "known" that was never a real login hides an org nobody can
  // then onboard. Showing a spurious row is the safer error -- onboarding it
  // is guarded by git_org_claim_conflict -- whereas hiding one leaves no route
  // forward at all.
  const knownGitOrgLogins = new Set(
    rows.flatMap((r) =>
      r.gitOrgLogins.length ? r.gitOrgLogins : [r.orgName.toLowerCase()]
    )
  );
  const newOrgs = (eligibleOrgs ?? []).filter(
    (org) => !knownGitOrgLogins.has(org.login.toLowerCase())
  );

  // Report the linked set up to the parent whenever it changes.
  useEffect(() => {
    if (!onLinkedOrgsChange || !info) return;
    onLinkedOrgsChange(
      rows
        .filter((r) => r.isLinked)
        .map((r) => ({ orgUuid: r.orgUuid, orgName: r.orgName }))
    );
    // rows is derived fresh from info/homeOrgUuid/homeOrgName every render;
    // depending on info (the actual state) instead of the recomputed array
    // keeps this from re-firing every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info, homeOrgUuid, homeOrgName, onLinkedOrgsChange]);

  const doLink = useCallback(
    async (orgUuid: string, label: string) => {
      if (writeInFlightRef.current) return;
      writeInFlightRef.current = true;
      setBusyOrg(orgUuid);
      const result = await linkOrgForCombining(orgUuid);
      // Success can be false with no Error string on a malformed/partial
      // response -- don't treat that as a success just because Error is
      // falsy.
      if (result.Error || !result.Success) {
        toast({
          title: `Couldn't link ${label}`,
          description: 'Please try again in a moment.',
          status: 'error',
          duration: 5000,
        });
      } else {
        toast({
          title: `${label} linked`,
          description: 'Your dashboards now include its data too.',
          status: 'success',
          duration: 5000,
        });
        // Reflect the known-successful write immediately -- don't make the
        // row's linked state depend solely on loadInfo()'s refetch landing
        // (and landing fresh). loadInfo() still runs below to reconcile the
        // rest of the card (e.g. a newly-onboarded org's membership row).
        setInfo((prev) =>
          prev && !prev.linkedOrgUuids.includes(orgUuid)
            ? { ...prev, linkedOrgUuids: [...prev.linkedOrgUuids, orgUuid] }
            : prev
        );
        await loadInfo();
        invalidateOrgScopedCaches();
      }
      writeInFlightRef.current = false;
      setBusyOrg(null);
    },
    [toast, loadInfo, invalidateOrgScopedCaches]
  );

  const doUnlink = useCallback(
    async (row: Row) => {
      if (writeInFlightRef.current) return;
      writeInFlightRef.current = true;
      setBusyOrg(row.orgUuid);
      const result = await unlinkOrgForCombining(row.orgUuid);
      // Same reasoning as doLink: Success can be false with no Error string
      // on a malformed/partial response.
      if (result.Error || !result.Success) {
        toast({
          title: `Couldn't unlink ${row.gitOrgLabel}`,
          description:
            result.Error === 'cannot_unlink_last_org'
              ? 'At least one organization has to stay linked.'
              : 'Please try again in a moment.',
          status: 'error',
          duration: 5000,
        });
      } else {
        toast({
          title: `${row.gitOrgLabel} unlinked`,
          description: row.isHome
            ? 'Your dashboards no longer include your home organization.'
            : 'Its data no longer appears in your dashboards.',
          status: 'success',
          duration: 5000,
        });
        // Same reasoning as doLink: reflect the known-successful write
        // immediately rather than waiting on the refetch.
        setInfo((prev) =>
          prev
            ? {
                ...prev,
                linkedOrgUuids: prev.linkedOrgUuids.filter(
                  (uuid) => uuid !== row.orgUuid
                ),
              }
            : prev
        );
        await loadInfo();
        invalidateOrgScopedCaches();
      }
      writeInFlightRef.current = false;
      setBusyOrg(null);
    },
    [toast, loadInfo, invalidateOrgScopedCaches]
  );

  const doOnboard = useCallback(
    async (org: GithubMemberOrg) => {
      if (writeInFlightRef.current || !user?.UserUUID) return;
      writeInFlightRef.current = true;
      setBusyOrg(org.login);
      const onboarded = await onboardLinkedOrg(
        user.UserUUID,
        org.login,
        org.id ? String(org.id) : undefined
      );
      writeInFlightRef.current = false;
      setBusyOrg(null);
      if (onboarded.Error || !onboarded.OrgUUID || !onboarded.MemberUserUUID) {
        toast({
          title: `Couldn't onboard ${org.login}`,
          description: 'Please try again in a moment.',
          status: 'error',
          duration: 5000,
        });
        return;
      }
      // Onboarding does not link -- the caller picks repos next, then gets
      // a toggle for this org same as any other already-onboarded one.
      // memberUserUuid is the caller's OWN row in the target org (not their
      // home-org UserUUID) -- repo-selection actions there must attribute
      // to that row. gitOrgLogin/gitOrgId scope the modal's repo-scope
      // validation and install URL to the target org, not the caller's
      // home org (ChooseRepositoriesModal defaults to home otherwise).
      setRepoModalOrg({
        orgUuid: onboarded.OrgUUID,
        label: org.login,
        memberUserUuid: onboarded.MemberUserUUID,
        gitOrgLogin: org.login,
        gitOrgId: org.id ? String(org.id) : undefined,
      });
    },
    [user?.UserUUID, toast]
  );

  const row = (r: Row) => {
    // Turning an unlinked org on is always safe, home included -- e.g. after
    // unlinking home to leave only one other org active, home must still be
    // toggleable to link it back. Turning a linked org off is only safe when
    // something else would remain linked (the "at least one org linked"
    // guardrail) -- when it's the only one, show a plain "Linked" label
    // instead of a toggle that can't be flipped.
    const showToggle = !r.isLinked || linkedCount > 1;
    return (
      <div
        key={r.orgUuid}
        className="flex items-center justify-between gap-3 px-3 py-2"
      >
        <span className="flex min-w-0 items-center gap-2">
          <span className="ds-text-code-md truncate text-ds-text-darker">
            {r.gitOrgLabel}
          </span>
          {r.isHome && (
            <span className="ds-text-body-xs-medium shrink-0 rounded-full bg-ds-brand-lightest px-2 py-0.5 text-ds-brand-subtle">
              Home
            </span>
          )}
        </span>
        <span className="flex shrink-0 items-center gap-3">
          {!r.isHome && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={busyOrg !== null || switching !== null}
              onClick={() => switchTo(r.orgUuid, r.gitOrgLabel)}
            >
              {switching === r.orgUuid ? 'Switching...' : 'Switch to this org'}
            </Button>
          )}
          {showToggle ? (
            <Switch
              checked={r.isLinked}
              disabled={busyOrg !== null}
              onCheckedChange={(checked) =>
                checked ? doLink(r.orgUuid, r.gitOrgLabel) : doUnlink(r)
              }
              aria-label={`${r.isLinked ? 'Unlink' : 'Link'} ${r.gitOrgLabel}`}
            />
          ) : (
            <span className="ds-text-body-xs-medium text-ds-text-subtle">
              Linked
            </span>
          )}
        </span>
      </div>
    );
  };

  const newOrgRow = (org: GithubMemberOrg) => (
    <div
      key={org.login}
      className="flex items-center justify-between gap-3 px-3 py-2"
    >
      <span className="ds-text-code-md truncate text-ds-text-darker">
        {org.login}
      </span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={busyOrg !== null}
        onClick={() => doOnboard(org)}
      >
        {busyOrg === org.login ? 'Onboarding...' : 'Onboard'}
      </Button>
    </div>
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <span className="ds-text-body-xs-medium text-ds-text-subtle">
          Link organizations
        </span>
        {sessionProvider === 'github' && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={openOrgAccessSettings}
          >
            <Plus className="size-4" />
            Add GitHub organization
          </Button>
        )}
      </div>

      <div className="divide-y divide-ds-border-subtlest overflow-hidden rounded-lg bg-ds-gray-subtlest">
        {info === null ? (
          [0, 1].map((i) => (
            <div key={i} className="px-3 py-2">
              <Skeleton className="h-5 w-40 bg-ds-surface-white" />
            </div>
          ))
        ) : rows.length ? (
          rows.map(row)
        ) : (
          <div className="ds-text-body-sm-medium px-3 py-2 text-ds-text-subtler">
            No organizations to link yet
          </div>
        )}

        {sessionProvider === 'github' &&
          (eligibleOrgs === null
            ? [0].map((i) => (
                <div key={i} className="px-3 py-2">
                  <Skeleton className="h-5 w-40 bg-ds-surface-white" />
                </div>
              ))
            : newOrgs.map(newOrgRow))}
      </div>

      <span className="ds-text-body-xs-medium text-pretty text-ds-text-subtle">
        Linking an organization adds its data to your own dashboards.
        Linking more than one combines their data together. It does not
        change which organization you are managing, and does not affect
        any other member&rsquo;s view. Switching signs you in as that
        organization&rsquo;s own user instead.
      </span>

      {sessionProvider === 'github' && (
        <span className="ds-text-body-xs-medium text-pretty text-ds-text-subtle">
          Missing an organization you belong to? Some organizations have to
          approve Entelligence before it can see them. Use Add GitHub
          organization to grant that access, then come back and it will appear
          here.
        </span>
      )}

      {repoModalOrg && (
        <ChooseRepositoriesModal
          open={repoModalOrg !== null}
          onOpenChange={(open) => {
            if (open) return;
            setRepoModalOrg(null);
            loadInfo();
          }}
          userUUID={repoModalOrg.memberUserUuid}
          orgUUID={repoModalOrg.orgUuid}
          targetGitOrgName={repoModalOrg.gitOrgLogin}
          targetGitOrgId={repoModalOrg.gitOrgId}
          onUpdated={loadInfo}
        />
      )}
    </div>
  );
}
